import { apiClient, filenameFromDisposition } from '@/api/client';
import type {
  Category,
  CreateManualFinding,
  DetectionStatusModel,
  DocumentModel,
  ExportMode,
  ExportedPdf,
  FindingModel,
  PageModel,
  UpdateCategory,
  UpdateFinding,
} from '@/types/documents.types';
import type { Paged } from '@/types/http.types';

/** The file name suffix the backend uses per mode, for when `Content-Disposition` is missing. */
const EXPORT_SUFFIX: Record<ExportMode, string> = {
  original: 'anotovano',
  redacted: 'redigovano',
  anonymized: 'anonymizovano',
};

/** `Smlouva.pdf` + `redacted` → `Smlouva_redigovano.pdf`. */
export const exportFilename = (originalName: string, mode: ExportMode): string => {
  const stem = originalName.replace(/\.pdf$/i, '');
  return `${stem}_${EXPORT_SUFFIX[mode]}.pdf`;
};

export const documentsApi = {
  // POST /documents — multipart upload; the backend inspects the PDF and opens a session.
  upload: async (file: File): Promise<DocumentModel> => {
    const form = new FormData();
    form.append('file', file, file.name);
    const { data } = await apiClient.post<DocumentModel>('/documents', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  // GET /documents — the caller's own open sessions, newest first. A bare array, not a page:
  // the backend holds a handful of documents per user at a time, so there is nothing to page.
  // Listing is a read-only peek; it does not push a document's idle expiry further out.
  list: async (): Promise<DocumentModel[]> => {
    const { data } = await apiClient.get<DocumentModel[]>('/documents');
    return data;
  },

  // GET /documents/{id}
  get: async (documentId: string): Promise<DocumentModel> => {
    const { data } = await apiClient.get<DocumentModel>(`/documents/${documentId}`);
    return data;
  },

  // DELETE /documents/{id} — discards the session (cancels a running detection).
  delete: async (documentId: string): Promise<void> => {
    await apiClient.delete(`/documents/${documentId}`);
  },

  // GET /documents/{id}/document — the PDF bytes for the viewer.
  downloadOriginal: async (documentId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/documents/${documentId}/document`, {
      responseType: 'blob',
    });
    return data;
  },

  // POST /documents/{id}/detection → 202 with the queued job.
  startDetection: async (documentId: string): Promise<DetectionStatusModel> => {
    const { data } = await apiClient.post<DetectionStatusModel>(`/documents/${documentId}/detection`);
    return data;
  },

  // POST /documents/{id}/detection/retry → 202 with the queued job. Re-runs the detector only on
  // the pages the last finished run skipped; findings elsewhere stay. 409 while a job is active
  // or when there is nothing to retry.
  retrySkippedPages: async (documentId: string): Promise<DetectionStatusModel> => {
    const { data } = await apiClient.post<DetectionStatusModel>(`/documents/${documentId}/detection/retry`);
    return data;
  },

  // GET /documents/{id}/detection
  detectionStatus: async (documentId: string): Promise<DetectionStatusModel> => {
    const { data } = await apiClient.get<DetectionStatusModel>(`/documents/${documentId}/detection`);
    return data;
  },

  // DELETE /documents/{id}/detection → 202, the job stops at its next checkpoint.
  cancelDetection: async (documentId: string): Promise<void> => {
    await apiClient.delete(`/documents/${documentId}/detection`);
  },

  // GET /documents/{id}/pages/{n}
  page: async (documentId: string, number: number): Promise<PageModel> => {
    const { data } = await apiClient.get<PageModel>(`/documents/${documentId}/pages/${number}`);
    return data;
  },

  // GET /documents/{id}/findings — every finding of the document, ordered by page and offset.
  findings: async (documentId: string): Promise<Paged<FindingModel>> => {
    const response = await apiClient.get<FindingModel[]>(`/documents/${documentId}/findings`);
    const total = Number(response.headers['x-total-count'] ?? response.data.length);
    return { items: response.data, total };
  },

  // POST /documents/{id}/findings → 201 with the new finding (source `user`).
  createFinding: async (documentId: string, body: CreateManualFinding): Promise<FindingModel> => {
    const { data } = await apiClient.post<FindingModel>(`/documents/${documentId}/findings`, body);
    return data;
  },

  // PUT /documents/{id}/findings/{fid} → 202
  updateFinding: async (documentId: string, findingId: string, body: UpdateFinding): Promise<void> => {
    await apiClient.put(`/documents/${documentId}/findings/${findingId}`, body);
  },

  // PUT /documents/{id}/categories/{category} → 202
  updateCategory: async (documentId: string, category: Category, body: UpdateCategory): Promise<void> => {
    await apiClient.put(`/documents/${documentId}/categories/${category}`, body);
  },

  // GET /documents/{id}/export?mode= — the PDF of the mode; 500 when verification failed.
  export: async (documentId: string, mode: ExportMode, originalName: string): Promise<ExportedPdf> => {
    const response = await apiClient.get<Blob>(`/documents/${documentId}/export`, {
      params: { mode },
      responseType: 'blob',
    });
    const filename =
      filenameFromDisposition(response.headers['content-disposition']) ?? exportFilename(originalName, mode);
    return { blob: response.data, filename };
  },

  // GET /documents/{id}/text?mode= — plain text without running headers and footers, for the
  // clipboard; 409 until detection has finished.
  text: async (documentId: string, mode: ExportMode): Promise<string> => {
    const { data } = await apiClient.get<string>(`/documents/${documentId}/text`, {
      params: { mode },
      responseType: 'text',
    });
    return data;
  },
};
